/**
 * Step 1 (Manual): Manual URL Input
 */

import { Card, Space, Input, Button, Row, Col, Statistic, Alert, Typography, message } from 'antd'
import {
  FormOutlined,
  LinkOutlined,
  GlobalOutlined,
  PhoneOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons'
import { isPhoneNumber } from '../utils'

const { Text, Title } = Typography
const { TextArea } = Input

interface ManualInputStepProps {
  projectName?: string | null
  manualUrlsText: string
  onTextChange: (text: string) => void
  subsource: string
  onSubsourceChange: (value: string) => void
  processingManualUrls: boolean
}

export default function ManualInputStep({
  projectName,
  manualUrlsText,
  onTextChange,
  subsource,
  onSubsourceChange,
  processingManualUrls,
}: ManualInputStepProps) {
  // Helper function to check if URL is a pure domain (no path)
  const isPureDomainUrl = (url: string): boolean => {
    try {
      const urlWithProtocol = url.startsWith('http') ? url : `https://${url}`
      const parsed = new URL(urlWithProtocol)
      return parsed.pathname === '/' || parsed.pathname === ''
    } catch {
      return true
    }
  }

  // Count URLs, domains, pages and phones from input
  const lines = manualUrlsText
    .split(/[\n\r]+/)
    .map(line => line.trim())
    .filter(line => line.length > 0)

  let phoneCount = 0
  let pureDomainCount = 0
  let pageCount = 0

  lines.forEach(line => {
    if (isPhoneNumber(line)) {
      phoneCount++
      return
    }

    if (isPureDomainUrl(line)) {
      pureDomainCount++
    } else {
      pageCount++
    }
  })

  const totalUrls = pureDomainCount + pageCount
  const hasData = totalUrls > 0 || phoneCount > 0

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText()
      onTextChange(manualUrlsText ? manualUrlsText + '\n' + text : text)
      message.success('Вставлено из буфера обмена')
    } catch {
      message.error('Не удалось получить текст из буфера обмена')
    }
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card
        bordered={false}
        style={{
          background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
          color: 'white'
        }}
      >
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Title level={4} style={{ margin: 0, color: 'white' }}>
            <FormOutlined /> Ручная загрузка ссылок
          </Title>
          <Text style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: 16 }}>
            {projectName || 'Проект не выбран'}
          </Text>
        </Space>
      </Card>

      <Card
        title={
          <Space>
            <LinkOutlined style={{ color: '#52c41a' }} />
            <span>Введите ссылки</span>
          </Space>
        }
        size="small"
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <TextArea
            placeholder={`Вставьте ссылки и телефоны (по одной строке):

example.com          → домен
example.com/page     → ссылка
+7 999 123-45-67     → телефон

Поддерживаются форматы:
• Полные URL (https://...)
• Без протокола (example.com)
• Российские номера (+7, 8...)`}
            value={manualUrlsText}
            onChange={(e) => onTextChange(e.target.value)}
            rows={10}
            style={{
              fontFamily: 'Monaco, Consolas, "Courier New", monospace',
              fontSize: 13,
              resize: 'vertical',
            }}
            disabled={processingManualUrls}
          />

          <Space wrap>
            <Button
              size="small"
              onClick={() => onTextChange('')}
              disabled={!manualUrlsText || processingManualUrls}
            >
              Очистить
            </Button>
            <Button
              size="small"
              onClick={handlePaste}
              disabled={processingManualUrls}
            >
              Вставить из буфера
            </Button>
          </Space>

          <div style={{ marginTop: 12 }}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
              Источник (subsource):
            </Text>
            <Input
              placeholder="yandex_parsing"
              value={subsource}
              onChange={(e) => onSubsourceChange(e.target.value)}
              style={{ maxWidth: 300 }}
              disabled={processingManualUrls}
            />
          </div>
        </Space>
      </Card>

      {/* Statistics - only show when there's data */}
      {hasData && (
        <Row gutter={12}>
          {pureDomainCount > 0 && (
            <Col span={pageCount > 0 ? (phoneCount > 0 ? 8 : 12) : (phoneCount > 0 ? 12 : 24)}>
              <Card size="small" style={{ background: '#f6ffed' }}>
                <Statistic
                  title={<span><GlobalOutlined style={{ marginRight: 4 }} />Домены</span>}
                  value={pureDomainCount}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
          )}
          {pageCount > 0 && (
            <Col span={pureDomainCount > 0 ? (phoneCount > 0 ? 8 : 12) : (phoneCount > 0 ? 12 : 24)}>
              <Card size="small" style={{ background: '#e6f7ff' }}>
                <Statistic
                  title={<span><LinkOutlined style={{ marginRight: 4 }} />Ссылки</span>}
                  value={pageCount}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
          )}
          {phoneCount > 0 && (
            <Col span={pureDomainCount > 0 || pageCount > 0 ? 8 : 24}>
              <Card size="small" style={{ background: '#fff7e6' }}>
                <Statistic
                  title={<span><PhoneOutlined style={{ marginRight: 4 }} />Телефоны</span>}
                  value={phoneCount}
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Card>
            </Col>
          )}
        </Row>
      )}

      <Alert
        message="Как это работает"
        description={
          <Space direction="vertical" size="small">
            <Text>1. Вставьте список ссылок (каждая ссылка на новой строке)</Text>
            <Text>2. Система автоматически извлечёт домены из ссылок</Text>
            <Text>3. Дубликаты будут удалены автоматически</Text>
            <Text>4. Домены пройдут AI анализ и ручное редактирование</Text>
            <Text>5. Выбранные домены будут загружены в VDL</Text>
          </Space>
        }
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
      />

      {lines.length > 0 && totalUrls === 0 && phoneCount === 0 && (
        <Alert
          message="Не удалось распознать данные"
          description="Проверьте формат введённых ссылок и телефонов. Убедитесь, что каждая запись находится на отдельной строке."
          type="warning"
          showIcon
        />
      )}
    </Space>
  )
}
